import { FC, Suspense, memo, useCallback, useEffect, useMemo, useTransition } from 'react';
import { addAddressPadding, WalletAccount } from 'starknet';
import { connect, disconnect } from 'starknetkit';
import { useNavigate } from 'react-router-dom';
import { ConnectOptions } from 'starknetkit';
import { getLastWallet, shortenAddress } from '../../../utils';
import { walletAccountAtom, walletAddressAtom, walletAtom } from '../../../atoms';
import Typography from '../Typography';
import Logo from '../Logo';
import { walletStarknetDomainAtom } from '../../../atoms/wallet';
import WalletPopup from './ WalletPopup/WalletPopup';
import { useAtom } from '../../../lib/state';
import ButtonPrimary from '../ButtonPrimary';
import ButtonTertiary from '../ButtonTertiary';
import walletConnectedAtom from '../../../atoms/wallet/walletConnectedAtom';
import walletPopupOpenAtom from '../../../atoms/wallet/walletPopupOpenAtom';
import { starknetConnectors } from '../../starknetConfig';

import './Wallet.scss';
import rpcProviderAtom from '../../../atoms/rpcProviderAtom';

interface WalletComponentProps {
  size?: 'lg' | 'sm';
  fullWidth?: boolean;
  connectLabel?: string;
  variant?: 'primary' | 'secondary';
}

const WalletComponent: FC<WalletComponentProps> = ({
  size = 'sm',
  fullWidth = false,
  connectLabel = 'Connect',
  variant = 'primary',
}) => {
  const [, startTransition] = useTransition();
  const navigate = useNavigate();

  const [walletAddress, setWalletAddress] = useAtom(walletAddressAtom);
  const [walletAccount, setWalletAccount] = useAtom(walletAccountAtom);
  const [walletStarknetDomain] = useAtom(walletStarknetDomainAtom);
  const [wallet, setWallet] = useAtom(walletAtom);
  const [, setWalletConnected] = useAtom(walletConnectedAtom);
  const [walletPopupOpen, setWalletPopupOpen] = useAtom(walletPopupOpenAtom);
  const [provider] = useAtom(rpcProviderAtom);

  const connectWallet = useCallback(
    async (disconnectFirst?: boolean, options?: Partial<ConnectOptions>) => {
      if (disconnectFirst) {
        try {
          await disconnect({
            clearLastWallet: true,
          });
        } catch {
          // Wallet may already be disconnected or null — safe to ignore
        }
      }

      let modalResult;
      try {
        modalResult = await connect({
          ...options,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          connectors: starknetConnectors as any,
        });
      } catch (error) {
        // User closed the wallet modal or rejected the connection request — not an error.
        console.warn('Wallet connection dismissed:', error);
        return false;
      }

      setWallet(modalResult.wallet ?? null);

      if (modalResult.wallet && modalResult.connectorData?.account && modalResult.connector) {
        try {
          const account = await modalResult.connector.account(provider);

          setWalletAddress(addAddressPadding(account.address));
          setWalletAccount(account);
          setWalletConnected(true);
        } catch (error) {
          // Wallet extensions can throw here (e.g. "wallet must has at least one account")
          console.warn('Failed to resolve wallet account:', error);
          return false;
        }
      }

      return Boolean(modalResult.wallet);
    },
    [provider, setWallet, setWalletAccount, setWalletAddress, setWalletConnected],
  );

  const handleAccountChangeEvent = useCallback(
    (accounts?: string[]) => {
      if (wallet && accounts?.[0]) {
        const account = new WalletAccount({ provider, walletProvider: wallet, address: accounts[0] });

        const setAccount = () => {
          if (account.address) {
            setWalletAddress(addAddressPadding(account.address));
            setWalletAccount(account);
            setWalletConnected(true);
          } else {
            setTimeout(setAccount, 500);
          }
        };

        setAccount();
      }
    },
    [wallet, provider, setWalletAddress, setWalletAccount, setWalletConnected],
  );

  const handleNetworkChangeEvent = useCallback(
    (chainId?: string, accounts?: string[]) => {
      if (wallet && accounts?.[0]) {
        const account = new WalletAccount({ provider, walletProvider: wallet, address: accounts[0] });

        const setAccount = () => {
          if (account.address) {
            setWalletAddress(addAddressPadding(account.address));
            setWalletAccount(account);
            setWalletConnected(true);
          } else {
            setTimeout(setAccount, 500);
          }
        };

        setAccount();

        // for safety, reload the app when account change
        navigate(0);
      }
    },
    [wallet, provider, navigate, setWalletAddress, setWalletAccount, setWalletConnected],
  );

  /**
   * Checks connection on app load - if user already connected wallet in previous session,
   * wallet will be automatically reconnected
   */
  useEffect(() => {
    const lastWallet = getLastWallet();

    // Only connect last connected wallet if there is no wallet connected
    if (lastWallet && !walletAccount) {
      connectWallet(false, { modalMode: 'neverAsk' }).catch(() => {
        // Auto-reconnect can fail (proposal expired, extension unavailable) — not critical.
      });
    } else {
      setWalletConnected(false);
    }
  }, [connectWallet, setWalletConnected, walletAccount]);

  useEffect(() => {
    if (wallet) {
      wallet.on('accountsChanged', handleAccountChangeEvent);
      wallet.on('networkChanged', handleNetworkChangeEvent);

      return () => {
        wallet.off('accountsChanged', handleAccountChangeEvent);
        wallet.off('networkChanged', handleNetworkChangeEvent);
      };
    }
  }, [handleAccountChangeEvent, handleNetworkChangeEvent, wallet]);

  /**
   * Called when user clicks on the Connect Wallet button
   */
  const handleConnect = useCallback(() => {
    setTimeout(async () => {
      try {
        await connectWallet(true);
      } catch {
        // Wallet connection can fail due to proposal expiry, modal dismissal, or
        // starknetkit internal errors — safe to ignore as connectWallet handles state.
      }
    }, 200);
  }, [connectWallet]);

  const handleWalletDisconnect = useCallback(async () => {
    try {
      await disconnect({
        clearLastWallet: true,
      });
    } catch {
      // Wallet may already be disconnected or null — safe to ignore
    }

    startTransition(() => {
      setWalletAddress(null);
      setWalletAccount(null);
      setWallet(null);
      setWalletPopupOpen(false);
      setWalletConnected(false);
    });
  }, [setWallet, setWalletAccount, setWalletAddress, setWalletConnected, setWalletPopupOpen]);

  const shortWalletAddress = useMemo(() => {
    if (!walletAddress) {
      return null;
    }

    return shortenAddress(walletAddress);
  }, [walletAddress]);

  const onCloseWalletPopup = useCallback(() => {
    setWalletPopupOpen(false);
  }, [setWalletPopupOpen]);

  const onToggleWalletPopup = useCallback(() => {
    setWalletPopupOpen(!walletPopupOpen);
  }, [setWalletPopupOpen, walletPopupOpen]);

  if (variant === 'secondary') {
    return (
      <div
        className="nostra__connectWalletModal__action"
        onClick={walletAccount ? () => {} : handleConnect}
        data-connected={Boolean(walletAccount)}
      >
        {walletAccount ? (
          <>
            <div className="nostra__connectWalletModal__actionTitle">
              <Logo type="token-STRK" size={24} />
              <Typography variant="body-md">{shortWalletAddress}</Typography>
            </div>
            <ButtonTertiary onClick={handleWalletDisconnect}>
              <Typography variant="button-sm" weight="semi-bold">
                Disconnect
              </Typography>
            </ButtonTertiary>
          </>
        ) : (
          <>
            <Typography variant="body-md">Connect Starknet wallet</Typography>
            <Logo type="token-STRK" size={24} />
          </>
        )}
      </div>
    );
  }

  return (
    <div style={{ width: fullWidth ? '100%' : '' }}>
      {walletAccount && (
        <div className="nostra__wallet__connected" onClick={onToggleWalletPopup} data-active={walletPopupOpen}>
          <Logo type="token-STRK" size={24} />
          <Typography
            className="nostra__wallet__connectedLabel"
            variant="button-sm"
            weight="semi-bold"
            color={walletPopupOpen ? 'text-primary-darkbg' : undefined}
          >
            {walletStarknetDomain ?? shortWalletAddress}
          </Typography>
        </div>
      )}
      {!walletAccount && (
        <ButtonPrimary onClick={handleConnect} size={size} fullWidth={fullWidth}>
          <Typography variant={`body-${size}`} weight="semi-bold" color="text-primary-darkbg">
            {connectLabel}
          </Typography>
        </ButtonPrimary>
      )}
      {walletPopupOpen && (
        <Suspense fallback={null}>
          <WalletPopup open={walletPopupOpen} onClose={onCloseWalletPopup} onDisconnect={handleWalletDisconnect} />
        </Suspense>
      )}
    </div>
  );
};

export const Wallet = memo(WalletComponent);
